import React from 'react';
import styles from './VeloxCard.module.css';

/**
 * The Velox card.
 *
 * One composition, rendered on three surfaces: the Contacts snapshot on Home,
 * the live preview on My Card, and the public Display Card page. Before this
 * existed each of those drew its own card and they had already drifted apart —
 * two different golds, three transition durations, two name sizes — so the
 * layout, palette and typography now live here and the surfaces only choose a
 * variant and which rows to show.
 *
 * Fields map exactly onto the card sub-document in models/User.js
 * (title, name, company, role, email, phone, linkedinURL, additionalURL);
 * Velox stores no card photo, so identity is carried by typography alone.
 */

const ICON_PROPS = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
};

function MailIcon({ size = 14 }) {
  return (
    <svg {...ICON_PROPS} width={size} height={size}>
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function PhoneIcon({ size = 14 }) {
  return (
    <svg {...ICON_PROPS} width={size} height={size}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7A2 2 0 0 1 22 16.9Z" />
    </svg>
  );
}

function LinkedInIcon({ size = 15 }) {
  return (
    <svg {...ICON_PROPS} width={size} height={size}>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function GlobeIcon({ size = 15 }) {
  return (
    <svg {...ICON_PROPS} width={size} height={size}>
      <circle cx="12" cy="12" r="9.5" />
      <path d="M2.5 12h19" />
      <path d="M12 2.5a15 15 0 0 1 4 9.5 15 15 0 0 1-4 9.5 15 15 0 0 1-4-9.5 15 15 0 0 1 4-9.5z" />
    </svg>
  );
}

/** Adds a scheme so a bare "veloxco.com" still resolves as an absolute URL. */
function absoluteHref(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** Drops the scheme and any trailing slash, so links read as labels. */
export function prettyUrl(url) {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  if (!trimmed) return '';
  return trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '').replace(/\/$/, '');
}

function ContactRow({ icon, children }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowIcon}>{icon}</span>
      <span className={styles.rowValue}>{children}</span>
    </div>
  );
}

export default function VeloxCard({
  card,
  /** `surface` sits inside the dark app shell; `presentation` stands alone. */
  variant = 'surface',
  /** `interactive` renders anchors, `static` renders inert marks, `none` hides. */
  links = 'none',
  showContact = true,
  /** Substitutes visible prompts for missing fields, for the My Card editor. */
  placeholders = false,
  footer = null,
  className = '',
  /** Optional company branding: { primaryColour, logoDataUrl }. Falls back to Velox gold. */
  brand = null,
}) {
  const data = card || {};
  const resolvedBrand = brand || data.branding || null;
  const brandColour = resolvedBrand?.primaryColour && /^#([0-9a-fA-F]{6})$/.test(resolvedBrand.primaryColour)
    ? resolvedBrand.primaryColour
    : null;
  const name = data.name || (placeholders ? 'Your name' : 'Name not available');
  const company = data.company || (placeholders ? 'Company' : '');
  const { role } = data;

  const linkedinHref = absoluteHref(data.linkedinURL);
  const websiteHref = absoluteHref(data.additionalURL);
  const hasLinks = links !== 'none' && (linkedinHref || websiteHref);
  const contactRows = showContact && (data.email || data.phone);

  return (
    <div
      className={[styles.card, styles[variant], className].filter(Boolean).join(' ')}
      style={brandColour ? { '--vx-gold': brandColour } : undefined}
    >
      <div className={styles.body}>
        {resolvedBrand?.logoDataUrl ? (
          <img className={styles.brandLogo} src={resolvedBrand.logoDataUrl} alt="" />
        ) : null}
        <div className={styles.identity}>
          <p className={styles.name}>
            {data.title ? <span className={styles.honorific}>{data.title} </span> : null}
            {name}
          </p>
          {role || company ? (
            <p className={styles.meta}>
              {role ? <span className={styles.metaStrong}>{role}</span> : null}
              {role && company ? <span className={styles.metaDot} aria-hidden="true" /> : null}
              {company ? <span>{company}</span> : null}
            </p>
          ) : null}
        </div>

        {contactRows || hasLinks ? <div className={styles.rule} aria-hidden="true" /> : null}

        {contactRows ? (
          <div className={styles.rows}>
            {data.email ? (
              <ContactRow icon={<MailIcon />}>{data.email}</ContactRow>
            ) : null}
            {data.phone ? (
              <ContactRow icon={<PhoneIcon />}>{data.phone}</ContactRow>
            ) : null}
          </div>
        ) : null}

        {hasLinks ? (
          <div className={styles.links}>
            {linkedinHref ? (
              links === 'interactive' ? (
                <a
                  className={styles.link}
                  href={linkedinHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LinkedInIcon />
                  <span>LinkedIn</span>
                </a>
              ) : (
                <span className={styles.link} aria-hidden="true">
                  <LinkedInIcon />
                  <span>LinkedIn</span>
                </span>
              )
            ) : null}
            {websiteHref ? (
              links === 'interactive' ? (
                <a
                  className={styles.link}
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GlobeIcon />
                  <span>{prettyUrl(data.additionalURL)}</span>
                </a>
              ) : (
                <span className={styles.link} aria-hidden="true">
                  <GlobeIcon />
                  <span>Website</span>
                </span>
              )
            ) : null}
          </div>
        ) : null}
      </div>

      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </div>
  );
}
